import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { Layout } from "./components/Layout";
import { getUser, getProjects, updateUserPlan, generateNotifications, logoutUser, validateCoupon } from "./services/apiService";
import { User, Project, PlanTier } from "./types";
import { LoginView } from "./components/LoginView";
import { AdminLoginView } from "./components/AdminLoginView";
import { DevLogin } from "./components/DevLogin";
import { ToastsOverlay } from "./components/Toast";

// Lazy load components for code splitting
const PricingPlans = lazy(() => import("./components/PricingPlans").then(m => ({ default: m.PricingPlans })));
const SettingsView = lazy(() => import("./components/SettingsView").then(m => ({ default: m.SettingsView })));
const AnalyticsView = lazy(() => import("./components/AnalyticsView").then(m => ({ default: m.AnalyticsView })));
const AIAnalyticsView = lazy(() => import("./components/AIAnalyticsView").then(m => ({ default: m.AIAnalyticsView })));
const FAQView = lazy(() => import("./components/FAQView").then(m => ({ default: m.FAQView })));
const ReleaseNotes = lazy(() => import("./components/ReleaseNotes").then(m => ({ default: m.ReleaseNotes })));
const ProfileCompletionView = lazy(() => import("./components/ProfileCompletionView").then(m => ({ default: m.ProfileCompletionView })));
const AdminPortal = lazy(() => import("./components/AdminPortal").then(m => ({ default: m.AdminPortal })));
const Dashboard = lazy(() => import("./components/Dashboard").then(m => ({ default: m.Dashboard })));
const CreateProject = lazy(() => import("./components/CreateProject").then(m => ({ default: m.CreateProject })));
const BoardView = lazy(() => import("./components/BoardView").then(m => ({ default: m.BoardView })));
const SupportWidget = lazy(() => import("./components/SupportWidget").then(m => ({ default: m.SupportWidget })));
const PageView = lazy(() => import("./components/PageView").then(m => ({ default: m.PageView })));
const ConfirmPlanView = lazy(() => import("./components/ConfirmPlanView").then(m => ({ default: m.ConfirmPlanView })));
const LeadCaptureForm = lazy(() => import("./components/LeadCaptureForm").then(m => ({ default: m.LeadCaptureForm })));
const UserTicketsView = lazy(() => import("./components/UserTicketsView").then(m => ({ default: m.UserTicketsView })));
const ActivityLog = lazy(() => import("./components/ActivityLog").then(m => ({ default: m.ActivityLog })));
const InvitesManagementView = lazy(() => import("./components/InvitesManagementView").then(m => ({ default: m.default })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
  </div>
);

const useHash = () => {
  const [hash, setHash] = useState(window.location.hash || '#/');
  useEffect(() => {
    const handler = () => setHash(window.location.hash || '#/');
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);
  return hash;
};

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const hash = useHash();
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessModalShown, setAccessModalShown] = useState(false);
  const [accessCouponCode, setAccessCouponCode] = useState("");
  const [accessCouponPlan, setAccessCouponPlan] = useState<PlanTier>(PlanTier.PRO);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);

  useEffect(() => {
    const initSession = async () => {
      try {
        // Check for OAuth callback FIRST - before any other initialization
        const currentHash = window.location.hash;
        if (currentHash === '#/oauth-callback' || currentHash.startsWith('#/oauth-callback')) {
          const urlParams = new URLSearchParams(window.location.search);
          const token = urlParams.get('token');
          const userData = urlParams.get('user');
          
          if (token && userData) {
            try {
              const parsedUser = JSON.parse(userData);
              localStorage.setItem('pf_token', token);
              localStorage.setItem('pf_user', JSON.stringify(parsedUser));
              setUser(parsedUser);
              // Clean URL
              window.history.replaceState({}, document.title, window.location.pathname);
              // Don't change hash yet - let the admin redirect useEffect or normal flow handle it
              // This prevents race conditions
              setIsInitializing(false);
              return; // Exit early - OAuth handled
            } catch (e) {
              console.error('Failed to parse OAuth callback data:', e);
              window.location.hash = '#/';
            }
          } else {
            // No valid OAuth params, redirect to home
            window.location.hash = '#/';
          }
        }
        
        // Normal session initialization
        const storedUser = await getUser();
        if (storedUser) { 
          setUser(storedUser);
          
          // Sync user data to localStorage for AI services (geminiService, openrouterService)
          // This ensures integrations persist across page refreshes and deployments
          try {
            localStorage.setItem('pf_user', JSON.stringify(storedUser));
          } catch (e) {
            console.warn('Failed to sync user to localStorage:', e);
          }
        }
      } catch (e) {
        console.error("Session init failed", e);
      } finally {
        setIsInitializing(false);
      }
    };
    initSession();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        try {
          console.log('[App] Fetching projects for user:', user.id);
          const projs = await getProjects(user.id);
          console.log('[App] Projects fetched successfully:', projs.length, 'projects');
          setProjects(projs);
          
          // Debug: Log when projects are fetched
          if (hash.startsWith('#/board/')) {
            const projectId = hash.split('/board/')[1];
            const found = projs.find(p => p.id === projectId);
            console.log(`[App] Hash changed to: ${hash}, Found project: ${found ? 'YES' : 'NO'}`);
            if (!found) {
              console.warn(`[App] Project ${projectId} not found in fetched projects. Fetched projects: ${projs.map(p => p.id).join(', ')}`);
            }
          }
        } catch (error) {
          console.error("[App] Failed to fetch projects:", error);
          // Don't clear projects on error - keep showing old data if any
        }
      } else {
        console.log('[App] No user logged in, skipping project fetch');
      }
    };
    fetchData();
  }, [user, hash]);

  // Handle redirect after admin login - using useEffect to ensure state is updated first
  useEffect(() => {
    if (user && user.role === 'admin' && (hash === '#/' || hash === '#/admin-login' || hash.startsWith('#/admin-login'))) {
      window.location.hash = '#/admin';
    }
  }, [user, hash]);

  const refreshUser = async () => {
    const refreshed = await getUser();
    if (refreshed) {
      setUser(refreshed);
    }
  };

  const hasPaidOrCoupon = (u: User) => {
    const hasCoupon = !!u.currentCouponCode || (u.currentDiscountPercent ?? 0) > 0;
    const isPaidPlan = u.plan !== PlanTier.KANBAN;
    return hasCoupon || isPaidPlan;
  };

  const allowedRestrictedRoutes = ['#/pricing', '#/settings'];
  const isAccessRestricted = user ? !hasPaidOrCoupon(user) : false;
  const isRouteAllowedWhenRestricted = allowedRestrictedRoutes.some(r => hash === r || hash.startsWith(r));

  useEffect(() => {
    if (user && isAccessRestricted && !accessModalShown) {
      setAccessCouponPlan(user.plan);
      setShowAccessModal(true);
      setAccessModalShown(true);
    }
  }, [user, isAccessRestricted, accessModalShown]);

  const handleApplyAccessCoupon = async () => {
    const code = accessCouponCode.trim();
    if (!code) {
      setAccessError('Please enter a coupon code.');
      return;
    }
    setAccessLoading(true);
    setAccessError(null);
    try {
      const result = await validateCoupon(code, accessCouponPlan);
      if (!result?.valid) {
        setAccessError(result?.message || 'Invalid coupon');
        return;
      }
      await refreshUser();
      setShowAccessModal(false);
    } catch (e: any) {
      setAccessError(e?.message || 'Failed to apply coupon');
    } finally {
      setAccessLoading(false);
    }
  };

  // Handle redirect after OAuth callback - redirect to dashboard for regular users
  useEffect(() => {
    if (!isInitializing && user && (hash === '#/oauth-callback' || hash.startsWith('#/oauth-callback'))) {
      // Redirect based on role
      if (user.role === 'admin') {
        window.location.hash = '#/admin';
      } else {
        window.location.hash = '#/';
      }
    }
  }, [user, hash, isInitializing]);

  const handleLogin = async (loggedInUser: User) => { 
    // Store user in localStorage for persistence
    localStorage.setItem('pf_user', JSON.stringify(loggedInUser));
    setUser(loggedInUser); 
    
    // Immediate redirect for admin users to prevent race conditions
    if (loggedInUser.role === 'admin' && (hash === '#/admin-login' || hash.startsWith('#/admin-login') || hash === '#/')) {
      window.location.hash = '#/admin';
    }
  };
  const handleLogout = () => { logoutUser(); setUser(null); setProjects([]); window.location.hash = '#/'; };
  const handleProjectDeleted = (projectId: string) => {
    setProjects(prev => prev.filter(p => p.id !== projectId));

    if (hash === `#/board/${projectId}` || hash === `#/analytics/${projectId}`) {
      window.location.hash = '#/';
    }
  };
  const notifications = useMemo(() => generateNotifications(projects), [projects]);

  if (isInitializing) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  // --- PUBLIC ROUTES ---
  if (hash.startsWith('#/page/')) { 
    const slug = hash.split('/page/')[1]; 
    return (
      <Suspense fallback={<LoadingFallback />}>
        <PageView slug={slug} onBack={() => window.history.back()} />
      </Suspense>
    ); 
  }
  if (hash.startsWith('#/confirm-change/')) { 
    return (
      <Suspense fallback={<LoadingFallback />}>
        <ConfirmPlanView />
      </Suspense>
    ); 
  }
  if (hash.startsWith('#/public/lead-form')) { 
    return (
      <Suspense fallback={<LoadingFallback />}>
        <LeadCaptureForm />
      </Suspense>
    ); 
  }
  // Handle OAuth callback - show loading while processing
  if (hash === '#/oauth-callback' || hash.startsWith('#/oauth-callback')) { return <LoadingFallback />; }
  
  // Dev login route - only show on localhost
  if (hash === '#/dev-login' && window.location.hostname === 'localhost') {
    if (user) {
      window.location.hash = '#/';
      return null;
    }
    return <DevLogin onLogin={handleLogin} />;
  }
  
  // Allow query params or trailing segments on admin login hash route
  // If already logged in as admin, redirect to admin portal
  if (hash === '#/admin-login' || hash.startsWith('#/admin-login')) { 
    if (user && user.role === 'admin') {
      window.location.hash = '#/admin';
      return null;
    }
    return <AdminLoginView onLogin={handleLogin} />; 
  }

  // --- PROTECTED ROUTES ---
  if (!user) { return <LoginView onLogin={handleLogin} />; }
  
  // Allow admins to bypass profile completion check
  if (hash === '#/admin' || hash.startsWith('#/admin')) {
    if (user.role === 'admin') { 
      return (
        <Suspense fallback={<LoadingFallback />}>
          <AdminPortal user={user} onLogout={handleLogout} />
        </Suspense>
      ); 
    }
    else { window.location.hash = '#/'; return null; }
  }
  
  // Regular users must complete profile (except OAuth users with oauth-pending phone)
  const needsProfileCompletion = !user.name || !user.email || (!user.phoneNumber && user.phoneNumber !== 'oauth-pending');
  if (needsProfileCompletion && user.phoneNumber !== 'oauth-pending') { 
    return (
      <Suspense fallback={<LoadingFallback />}>
        <ProfileCompletionView user={user} onComplete={(u) => setUser(u)} />
      </Suspense>
    ); 
  }

  const AccessLimited = () => (
    <div className="flex flex-col items-center justify-center h-full text-slate-700 gap-4 py-10">
      <div className="text-xl font-semibold">Upgrade or apply a coupon</div>
      <p className="text-sm text-slate-500 text-center max-w-md">
        Your workspace is limited until you add a payment method or apply a valid coupon code. Profile and settings remain available.
      </p>
      <div className="flex gap-3 flex-wrap justify-center">
        <button onClick={() => setShowAccessModal(true)} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
          Apply Coupon
        </button>
        <button onClick={() => window.location.hash = '#/pricing'} className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800">
          Upgrade Plan
        </button>
        <button onClick={() => window.location.hash = '#/settings'} className="px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded hover:bg-slate-50">
          Go to Settings
        </button>
      </div>
    </div>
  );

  let content;
  if (isAccessRestricted && !isRouteAllowedWhenRestricted) {
    content = <AccessLimited />;
  } else if (hash === '#/pricing') {
    content = <PricingPlans currentPlan={user.plan} onSelectPlan={(p) => setUser(updateUserPlan(p))} user={user} onCouponApplied={refreshUser} />;
  } else if (hash.startsWith('#/checkout-success')) {
    content = <div className="flex flex-col items-center justify-center h-full text-green-600 gap-4">
      <div className="text-2xl font-bold">Payment Successful!</div>
      <div className="text-lg">Your plan has been upgraded.</div>
      <button onClick={() => window.location.hash = '#/'} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Return to Dashboard</button>
    </div>;
  } else if (hash === '#/create') {
    content = <CreateProject user={user} />;
  } else if (hash === '#/settings') {
    content = <SettingsView user={user} onCouponApplied={refreshUser} />;
  } else if (hash === '#/faq') {
    content = <FAQView />;
  } else if (hash === '#/tickets') {
    content = <UserTicketsView user={user} />;
  } else if (hash === '#/activity') {
    content = <ActivityLog onBack={() => window.location.hash = '#/'} />;
  } else if (hash === '#/invites') {
    content = <InvitesManagementView />;
  } else if (hash.startsWith('#/board/')) {
    const boardSlug = hash.split('/board/')[1];
    const projectId = boardSlug.split('?')[0];
    const project = projects.find(p => p.id === projectId);
    
    // If project not found, show loading with retry attempt
    if (!project) {
      content = (
        <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-4">
          <div className="animate-pulse mb-4 text-lg">Loading Board...</div>
          <p className="text-sm text-slate-400">Looking for project {projectId}</p>
          <button 
            onClick={() => {
              // Force refetch projects
              if (user) {
                getProjects(user.id).then(setProjects).catch(console.error);
              }
            }}
            className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Retry
          </button>
        </div>
      );
    } else {
      content = <BoardView project={project} user={user} />;
    }
  } else if (hash.startsWith('#/analytics/')) {
    const analyticsSlug = hash.split('/analytics/')[1];
    const projectId = analyticsSlug.split('?')[0];
    const project = projects.find(p => p.id === projectId);
    content = project ? <AnalyticsView project={project} onBack={() => window.location.hash = '#/'} /> : <div>Project not found</div>;
  } else if (hash.startsWith('#/ai-analytics/')) {
    const aiAnalyticsSlug = hash.split('/ai-analytics/')[1];
    const projectId = aiAnalyticsSlug.split('?')[0];
    const project = projects.find(p => p.id === projectId);
    content = project ? <AIAnalyticsView project={project} onBack={() => window.location.hash = `#/analytics/${projectId}`} /> : <div>Project not found</div>;
  } else {
    content = <Dashboard projects={projects} onProjectDeleted={handleProjectDeleted} />;
  }

  return (
    <>
      <Suspense fallback={<LoadingFallback />}>
        <Layout user={user} fullWidth={hash.startsWith('#/board')} currentPath={hash} notifications={notifications} onLogout={handleLogout} restrictedAccess={isAccessRestricted}>
          {content}
        </Layout>
        <SupportWidget user={user} />
        <ReleaseNotes />
        {showAccessModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Enter Coupon Code</h3>
                <button onClick={() => setShowAccessModal(false)} className="text-slate-500 hover:text-slate-700">✕</button>
              </div>
              <p className="text-sm text-slate-600">Apply a valid coupon to unlock the workspace without payment.</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Coupon Code</label>
                  <input
                    value={accessCouponCode}
                    onChange={(e) => setAccessCouponCode(e.target.value.toUpperCase())}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ENTER CODE"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Plan</label>
                  <div className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700">
                    {accessCouponPlan}
                  </div>
                </div>
                {accessError && <div className="text-sm text-red-600">{accessError}</div>}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleApplyAccessCoupon}
                  disabled={accessLoading}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-300"
                >
                  {accessLoading ? 'Applying...' : 'Apply Coupon'}
                </button>
                <button
                  onClick={() => window.location.hash = '#/pricing'}
                  className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                >
                  Upgrade Plan
                </button>
              </div>
            </div>
          </div>
        )}
      </Suspense>
      <ToastsOverlay />
    </>
  );
}